import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

export const TOTAL_COINS = 100000;
export const pctStr = (n, d = 1) =>
  (Number.isFinite(n) ? `${(n * 100).toFixed(d)}%` : "—");

export function HelpDot({ tip }) {
  if (!tip) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" aria-label="?" className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-zinc-400 text-zinc-500 text-[8px] font-bold mx-1 hover:bg-black hover:text-white hover:border-black transition shrink-0">
            ?
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function StageShell({ step, role, title, desc, accentBg, accentText, children }) {
  return (
    <div className="border border-black bg-white grid grid-cols-12 overflow-hidden">
      <div className={`col-span-12 md:col-span-1 ${accentBg} flex md:flex-col items-center md:items-stretch md:justify-start gap-4 p-4 md:py-7`}>
        <div className={`font-display text-3xl md:text-5xl font-black ${accentText} tabular-nums leading-none`}>{step}</div>
        <div className={`font-mono-label text-[10px] ${accentText} md:[writing-mode:vertical-rl] md:rotate-180 mt-auto`}>{role}</div>
      </div>
      <div className="col-span-12 md:col-span-11 p-6 md:p-8">
        <div className="font-mono-label text-[10px] text-zinc-600">⎯⎯ {role}</div>
        <h3 className="font-display text-2xl md:text-3xl font-black mt-2 tracking-tight">{title}</h3>
        <p className="text-zinc-600 text-sm mt-2 max-w-2xl leading-relaxed">{desc}</p>
        {children}
      </div>
    </div>
  );
}

export function ColumnHeader({ label, accent = "text-zinc-500" }) {
  return (
    <div className={`font-mono-label text-[10px] ${accent} mb-3 flex items-center gap-2`}>
      <span className="inline-block w-4 h-px bg-current" />
      {label}
    </div>
  );
}

export function Metric({ k, v, accent, big, tip }) {
  // Sizes tuned to fit large monetary values inside narrow columns at md/lg breakpoints
  const sizeClass = big
    ? "text-lg md:text-xl xl:text-2xl"
    : "text-base md:text-lg";
  return (
    <div className="min-w-0 overflow-hidden">
      <div className="font-mono-label text-[9px] text-zinc-500 truncate flex items-center">
        <span className="truncate">{k}</span>
        <HelpDot tip={tip} />
      </div>
      <div className={`font-display ${sizeClass} font-black mt-1 tabular-nums ${accent || ""} truncate`}>
        {v}
      </div>
    </div>
  );
}

export function Arrow() {
  return (
    <div className="flex justify-center my-3 md:my-4">
      <div className="w-px h-12 bg-black/30" />
    </div>
  );
}

export function SliderBlock({ id, label, tip, valueLabel, value, min, max, step, onChange }) {
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <Label className="font-mono-label text-[10px] text-zinc-600 flex items-center">
          <span>{label}</span>
          <HelpDot tip={tip} />
        </Label>
        <span className="font-display text-sm font-bold tabular-nums">{valueLabel}</span>
      </div>
      <Slider
        data-testid={`jc-${id}`}
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        className="mt-2"
      />
    </div>
  );
}

export function NumField({ id, label, tip, value, onChange, min = 0, step = 1, suffix }) {
  return (
    <div>
      <Label className="font-mono-label text-[10px] text-zinc-600 flex items-center">
        <span>{label}</span>
        <HelpDot tip={tip} />
      </Label>
      <div className="mt-2 flex items-center border border-black/30 bg-white focus-within:border-black">
        <Input
          data-testid={`jc-${id}`}
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="rounded-none border-0 h-10 focus-visible:ring-0 text-black bg-white"
        />
        {suffix && (
          <span className="px-3 text-zinc-500 font-mono-label text-[10px]">{suffix}</span>
        )}
      </div>
    </div>
  );
}
